import React from "react";
import { Stack } from "@fluentui/react";
import { ComponentInstanceSummary } from "../../../api/models/component-instance-summary";
import { CoObjectTypeEnum } from "../../../api/models";
import { generateIdentifier } from "../../../utils/helpers/RobotFramework";

// Every value CoObjectTypeEnum declares is a "known" object type with a matching CSS icon rule in base.css
// (e.g. .TABLE, .AMOUNT - see base.css). Anything else is unmapped (FR-007 EDGE03) and must render without an
// icon span rather than emit a class the CSS never styles.
const KNOWN_OBJECT_TYPES: ReadonlySet<string> = new Set(Object.values(CoObjectTypeEnum));

export interface InstanceRowProps {
  instance: ComponentInstanceSummary;
}

/**
 * Renders one `ComponentInstanceSummary` as a completely inert row: the object-type icon, the instance's own
 * name, and the component-instance marker. Designed to be placed inside a list (IDS-7054 T14 `InstanceList`)
 * that renders many of these, so this component only ever knows about a single instance.
 *
 * Deliberately NOT a reuse of `ObjectListItemRow` (decision P5, IDS-7054 T10). `ObjectListItemRow`'s `ItemLabel`
 * always wraps the label in a Fluent `<Link>` carrying an `onClick`, and Fluent's `useLink` resolves the link to
 * a native, focusable `<button>` whenever neither `as` nor `href` is supplied - even with no real behavior wired
 * up. That would violate constraint A6 ("instance rows are not focusable") and scenario FR26. This component
 * reproduces the *visual* of `ObjectListItemRow.tsx`'s `ItemLabel` using the exact same CSS classes and
 * truncation idiom, but with no focusable or clickable element anywhere. `ObjectListItemRow` itself and its
 * callers (`ObjectList.tsx`, `ObjectListItemRowExpandable.tsx`, `AskAiReviewSaveModal.tsx`, `TagsPanel.tsx`,
 * `TableTotalsRuleEditor.tsx`) are left untouched.
 *
 * @export
 * @param {InstanceRowProps} props
 */
export const InstanceRow: React.FC<InstanceRowProps> = ({ instance }) => {
  // FR-007 EDGE03: an unmapped objectType must render without a type icon rather than fail.
  const hasKnownType = KNOWN_OBJECT_TYPES.has(instance.objectType);

  return (
    <Stack
      horizontal
      data-testid="instance-row"
      id={generateIdentifier(instance.objectName, "instancerow")}
      style={{ alignItems: "center", padding: "1px 0", width: "100%" }}
    >
      <div
        style={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {hasKnownType ? <span className={`${instance.objectType} icon_class`} style={{ lineHeight: 1 }} /> : null}
        {/* FR-007 / A5: the instance's own name only - never componentReference.objectName */}
        <span style={{ lineHeight: 1 }}>{instance.objectName}</span>
        {/* Every row is, by definition, an instance, so the marker is unconditional (FR-007) */}
        <span className="instance_component_title right_icon_class" />
      </div>
    </Stack>
  );
};
